import { ADD_COST_PER_VALUE, MULTIPLY_COST_PER_VALUE } from "@/program/Card";
import {
  OrbColor,
  ORB_COLORS,
  ORB_COLOR_LABELS,
} from "@/program/VariableState";

/**
 * 足す/かけるカードの値の選択肢
 */
const ADD_VALUE_OPTIONS = [1, 2, 3, 5, 10, 20] as const;
const MULTIPLY_VALUE_OPTIONS = [2, 3, 5, 10] as const;

export type OrbCardMode = "add" | "multiply";

export interface OrbSelection {
  operator: OrbCardMode;
  color: OrbColor;
  value: number;
}

/**
 * オーブ選択モーダル（オーブカード用）
 *
 * 演算子・色・値を選択する3段構成。値クリックはハイライト保持し、
 * 決定ボタンで確定する。演算子変更時は selectedValue をリセットする。
 */
export class OrbSelectorModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private onSelectCallback?: (selection: OrbSelection) => void;
  private onCloseCallback?: () => void;
  private isOpen: boolean = false;
  private selectedOperator: OrbCardMode = "add";
  private availableMP: number = Infinity;
  private selectedColor: OrbColor = "green";
  private selectedValue: number | null = null;
  private previousFocus: HTMLElement | null = null;

  constructor() {
    this.overlay = document.createElement("div");
    this.overlay.className = "orb-modal-overlay";
    this.overlay.addEventListener("click", () => this.close());

    this.modal = document.createElement("div");
    this.modal.className = "orb-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.modal.setAttribute("aria-labelledby", "orb-modal-title");
    this.modal.addEventListener("click", (e) => e.stopPropagation());

    this.overlay.appendChild(this.modal);
  }

  /**
   * 新規追加用モーダルを開く（デフォルト: 足す・緑・1）
   */
  openForNew(availableMP: number): void {
    this.open("add", "green", 1, availableMP);
  }

  /**
   * モーダルを開く
   * @param currentValue 編集時の既存値。null なら未選択状態で開く。
   */
  open(
    currentOperator: OrbCardMode,
    currentColor: OrbColor,
    currentValue: number | null,
    availableMP: number
  ): void {
    if (this.isOpen) return;
    this.isOpen = true;

    this.selectedOperator = currentOperator;
    this.availableMP = availableMP;
    this.selectedColor = currentColor;
    this.selectedValue = currentValue;
    this.previousFocus = document.activeElement as HTMLElement | null;
    this.render();
    document.body.appendChild(this.overlay);

    document.addEventListener("keydown", this.handleKeyDown);

    // 最初のフォーカス可能な要素にフォーカス
    requestAnimationFrame(() => {
      const first = this.modal.querySelector("button:not([disabled])");
      if (first instanceof HTMLElement) first.focus();
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.overlay.remove();
    document.removeEventListener("keydown", this.handleKeyDown);
    this.previousFocus?.focus();
    this.onCloseCallback?.();
  }

  getIsOpen(): boolean {
    return this.isOpen;
  }

  onSelect(callback: (selection: OrbSelection) => void): void {
    this.onSelectCallback = callback;
  }

  onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.close();
    } else if (e.key === "Tab") {
      this.trapFocus(e);
    }
  };

  /** モーダル内でフォーカスを循環させる */
  private trapFocus(e: KeyboardEvent): void {
    const focusable = this.modal.querySelectorAll("button:not([disabled])");
    if (focusable.length === 0) return;

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private getValueOptions(): readonly number[] {
    return this.selectedOperator === "add"
      ? ADD_VALUE_OPTIONS
      : MULTIPLY_VALUE_OPTIONS;
  }

  private getCostPerValue(): number {
    return this.selectedOperator === "add"
      ? ADD_COST_PER_VALUE
      : MULTIPLY_COST_PER_VALUE;
  }

  private render(): void {
    const children: HTMLElement[] = [];

    // タイトル
    const title = document.createElement("h2");
    title.id = "orb-modal-title";
    title.className = "orb-modal__title";
    title.textContent = "オーブカードの設定";
    children.push(title);

    // 演算子選択
    const operatorSection = document.createElement("div");
    operatorSection.className = "orb-modal__operator-section";

    const operatorLabel = document.createElement("div");
    operatorLabel.className = "orb-modal__section-label";
    operatorLabel.textContent = "演算子";
    operatorSection.appendChild(operatorLabel);

    const operatorGrid = document.createElement("div");
    operatorGrid.className = "orb-modal__operator-grid";
    operatorGrid.setAttribute("role", "group");
    operatorGrid.setAttribute("aria-label", "演算子");

    const operators: { mode: OrbCardMode; label: string; ariaLabel: string }[] =
      [
        { mode: "add", label: "＋（足す）", ariaLabel: "足す" },
        { mode: "multiply", label: "×（かける）", ariaLabel: "かける" },
      ];

    for (const { mode, label, ariaLabel } of operators) {
      const button = document.createElement("button");
      button.className = "orb-modal__operator-option";
      if (mode === this.selectedOperator) {
        button.classList.add("orb-modal__operator-option--selected");
      }
      button.textContent = label;
      button.setAttribute("aria-label", ariaLabel);
      button.setAttribute(
        "aria-pressed",
        String(mode === this.selectedOperator)
      );
      button.addEventListener("click", () => {
        this.selectedOperator = mode;
        // 演算子変更時は値をリセット（選択肢が変わるため）
        this.selectedValue = null;
        this.render();
      });
      operatorGrid.appendChild(button);
    }

    operatorSection.appendChild(operatorGrid);
    children.push(operatorSection);

    // 色選択
    const colorSection = document.createElement("div");
    colorSection.className = "orb-modal__color-section";

    const colorLabel = document.createElement("div");
    colorLabel.className = "orb-modal__section-label";
    colorLabel.textContent = "オーブの色";
    colorSection.appendChild(colorLabel);

    const colorGrid = document.createElement("div");
    colorGrid.className = "orb-modal__color-grid";

    for (const color of ORB_COLORS) {
      const button = document.createElement("button");
      button.className = "orb-modal__color-option";
      if (color === this.selectedColor) {
        button.classList.add("orb-modal__color-option--selected");
      }
      button.setAttribute("aria-label", `${ORB_COLOR_LABELS[color]}オーブ`);

      const indicator = document.createElement("span");
      indicator.className = `orb-modal__color-indicator orb-color--${color} orb-glow`;
      button.appendChild(indicator);

      const label = document.createElement("span");
      label.textContent = ORB_COLOR_LABELS[color];
      button.appendChild(label);

      button.addEventListener("click", () => {
        this.selectedColor = color;
        this.render();
      });

      colorGrid.appendChild(button);
    }

    colorSection.appendChild(colorGrid);
    children.push(colorSection);

    // 値選択
    const valueSection = document.createElement("div");
    valueSection.className = "orb-modal__value-section";

    const valueLabel = document.createElement("div");
    valueLabel.className = "orb-modal__section-label";
    valueLabel.textContent =
      this.selectedOperator === "add" ? "足す値" : "かける値";
    valueSection.appendChild(valueLabel);

    const valueGrid = document.createElement("div");
    valueGrid.className = "orb-modal__value-grid";

    const costPerValue = this.getCostPerValue();

    for (const value of this.getValueOptions()) {
      const cost = value * costPerValue;
      const isDisabled = cost > this.availableMP;
      const isSelected = value === this.selectedValue;

      const button = document.createElement("button");
      button.className = "orb-modal__value-option";
      if (isSelected) button.classList.add("orb-modal__value-option--selected");
      if (isDisabled) button.classList.add("orb-modal__value-option--disabled");
      button.disabled = isDisabled;
      button.setAttribute(
        "aria-label",
        `${value}（コスト${cost}）${isDisabled ? "MP不足" : ""}`
      );

      const valueSpan = document.createElement("span");
      valueSpan.className = "orb-modal__value";
      valueSpan.textContent = String(value);
      button.appendChild(valueSpan);

      const costSpan = document.createElement("span");
      costSpan.className = "orb-modal__cost";
      costSpan.textContent = `${cost}MP`;
      button.appendChild(costSpan);

      if (!isDisabled) {
        button.addEventListener("click", () => {
          this.selectedValue = value;
          this.render();
        });
      }

      valueGrid.appendChild(button);
    }

    valueSection.appendChild(valueGrid);
    children.push(valueSection);

    // 確定ボタン
    const confirmBtn = document.createElement("button");
    confirmBtn.className = "orb-modal__confirm";
    confirmBtn.textContent = "決定";
    confirmBtn.disabled = this.selectedValue === null;
    confirmBtn.addEventListener("click", () => {
      if (this.selectedValue !== null) {
        this.onSelectCallback?.({
          operator: this.selectedOperator,
          color: this.selectedColor,
          value: this.selectedValue,
        });
        this.close();
      }
    });
    children.push(confirmBtn);

    // キャンセル
    const closeBtn = document.createElement("button");
    closeBtn.className = "orb-modal__close";
    closeBtn.textContent = "キャンセル";
    closeBtn.addEventListener("click", () => this.close());
    children.push(closeBtn);

    this.modal.replaceChildren(...children);
  }
}
